import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, DatePicker, message, Popover, Segmented, Spin, Tag } from "antd";
import { EditOutlined, LeftOutlined, RightOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { capitalizeFirstLetter } from "../../../helpers/auth/helper";
import useIsMobile from "../../../hooks/useIsMobile";
import { updateAppointmentApi } from "../../../services/receptionist.services";
import EditAppointmentModal from "./EditAppointmentModal";
import tagStyles from "../../../styles/Custom/Tag.module.css";
import styles from "./AppointmentsCalendar.module.css";

const HOUR_HEIGHT = 48; 
const GUTTER = 56; 
const DEFAULT_DURATION_MIN = 30;
const MONTH_CELL_EVENTS = 3;
const MONTH_EVENT_ROW = 20; 
const MONTH_DATE_HEIGHT = 22; 
const MONTH_CELL_PADDING = 10; 
const MONTH_MORE_ROW = 16; 
const MONTH_ROW_MIN =
  MONTH_DATE_HEIGHT + MONTH_CELL_PADDING + MONTH_EVENT_ROW + MONTH_MORE_ROW;
const MAX_LANES_WEEK = 2;
const MAX_LANES_DAY = 4;

const DOCTOR_COLORS = [
  { bg: "#f4ebff", bar: "#9e77ed" },
  { bg: "#eff8ff", bar: "#2e90fa" },
  { bg: "#ecfdf3", bar: "#12b76a" },
  { bg: "#fff6ed", bar: "#fb6514" },
  { bg: "#fdf2fa", bar: "#ee46bc" },
  { bg: "#f0f9ff", bar: "#06aed4" },
  { bg: "#fefbe8", bar: "#eaaa08" },
  { bg: "#fef3f2", bar: "#f04438" },
];
const CANCELLED_COLOR = { bg: "#f2f4f7", bar: "#98a2b3" };

const doctorColor = (record) => {
  if (record?.status === "cancelled") return CANCELLED_COLOR;
  const id = Number(record?.doctor);
  if (!id) return CANCELLED_COLOR;
  return DOCTOR_COLORS[Math.abs(id) % DOCTOR_COLORS.length];
};

export const startOfWeek = (date) => {
  const dayOfWeek = date.day();
  return date.subtract(dayOfWeek === 0 ? 6 : dayOfWeek - 1, "day").startOf("day");
};

export const calendarRange = (anchor, mode) => {
  if (mode === "month") {
    const monthStart = anchor.startOf("month");
    const start = startOfWeek(monthStart);
    const weeks = Math.ceil(
      (monthStart.diff(start, "day") + anchor.daysInMonth()) / 7
    );
    return { start, end: start.add(weeks * 7 - 1, "day"), weeks };
  }
  if (mode === "day") {
    const start = anchor.startOf("day");
    return { start, end: start, weeks: 1 };
  }
  const start = startOfWeek(anchor);
  return { start, end: start.add(6, "day"), weeks: 1 };
};

const statusColor = (status) =>
  status === "reported" ? "green" : status === "scheduled" ? "orange" : "magenta";

const toMinutes = (time) => {
  if (!time) return null;
  const [h, m] = String(time).split(":");
  const hours = Number(h);
  const mins = Number(m);
  if (Number.isNaN(hours) || Number.isNaN(mins)) return null;
  return hours * 60 + mins;
};

const minutesToLabel = (mins) =>
  dayjs().startOf("day").add(mins, "minute").format("h:mm A");

const patientName = (record) =>
  `${
    record?.patient_first_name
      ? capitalizeFirstLetter(record.patient_first_name)
      : ""
  } ${
    record?.patient_last_name
      ? capitalizeFirstLetter(record.patient_last_name)
      : ""
  }`.trim() || "Unnamed patient";

const eventStyle = (record) => {
  const c = doctorColor(record);
  return { background: c.bg, borderLeftColor: c.bar };
};

const layoutOverlaps = (events, maxLanes) => {
  const sorted = [...events].sort(
    (a, b) => a.startMin - b.startMin || a.endMin - b.endMin
  );
  const placed = [];
  const overflows = [];
  let cluster = [];
  let clusterEnd = -1;

  const flush = () => {
    const laneEnds = [];
    cluster.forEach((ev) => {
      let lane = laneEnds.findIndex((end) => end <= ev.startMin);
      if (lane === -1) {
        laneEnds.push(ev.endMin);
        lane = laneEnds.length - 1;
      } else {
        laneEnds[lane] = ev.endMin;
      }
      ev.lane = lane;
    });

    const laneCount = laneEnds.length;
    if (laneCount <= maxLanes) {
      cluster.forEach((ev) => {
        ev.laneCount = laneCount;
        placed.push(ev);
      });
    } else {
      const visibleLanes = Math.max(1, maxLanes - 1);
      const laneTotal = visibleLanes + 1;
      const hidden = [];
      cluster.forEach((ev) => {
        if (ev.lane < visibleLanes) {
          ev.laneCount = laneTotal;
          placed.push(ev);
        } else {
          hidden.push(ev);
        }
      });
      overflows.push({
        key: `more-${cluster[0].record.id}`,
        lane: visibleLanes,
        laneCount: laneTotal,
        startMin: Math.min(...hidden.map((ev) => ev.startMin)),
        endMin: Math.max(...hidden.map((ev) => ev.endMin)),
        hidden: hidden.sort((a, b) => a.startMin - b.startMin),
      });
    }

    cluster = [];
    clusterEnd = -1;
  };

  sorted.forEach((ev) => {
    if (cluster.length && ev.startMin >= clusterEnd) flush();
    cluster.push(ev);
    clusterEnd = Math.max(clusterEnd, ev.endMin);
  });
  if (cluster.length) flush();

  return { placed, overflows };
};

const AppointmentDetails = ({ record, doctorRole, onEdit, onRefresh }) => {
  const [saving, setSaving] = useState(null);
  const start = toMinutes(record?.appointment_time);
  const end = toMinutes(record?.appointment_end_time);
  const timeText =
    start === null
      ? "—"
      : `${minutesToLabel(start)}${end !== null ? ` – ${minutesToLabel(end)}` : ""}`;

  const rows = [
    { label: "Patient ID", value: record?.patient ?? "—" },
    {
      label: "Date",
      value: record?.appointment_date
        ? dayjs(record.appointment_date).format("DD MMM, YYYY")
        : "—",
    },
    { label: "Time", value: timeText },
    {
      label: "Reason",
      value: record?.reason_to_visit || "Not provided",
      muted: !record?.reason_to_visit,
    },
    { label: "Doctor", value: record?.doctor_name || "—" },
    {
      label: "Role",
      value: doctorRole ? doctorRole.replace(/_/g, " ") : "—",
      muted: !doctorRole,
    },
  ];

  const setStatus = async (status) => {
    setSaving(status);
    try {
      const res = await updateAppointmentApi({
        appointment_id: record?.id,
        status,
      });
      message.success(res?.data?.message || "Appointment updated");
      onRefresh?.();
    } catch (err) {
      message.error(
        err?.response?.data?.message || "Could not update the appointment"
      );
    } finally {
      setSaving(null);
    }
  };

  const isCancelled = record?.status === "cancelled";
  const isReported = record?.status === "reported";
  const isClosed = isCancelled || isReported;

  return (
    <div className={styles.popCard}>
      <div className={styles.popHeader}>
        <span className={styles.popNameWrap}>
          <span className={styles.popName}>{patientName(record)}</span>
          {!isClosed && (
            <Button
              type="text"
              size="small"
              aria-label="Edit appointment"
              className={styles.popEditBtn}
              icon={<EditOutlined />}
              onClick={() => onEdit?.(record)}
            />
          )}
        </span>
        <Tag color={statusColor(record?.status)} className={tagStyles.mdRndTag}>
          {capitalizeFirstLetter(record?.status || "")}
        </Tag>
      </div>
      <div className={styles.popRows}>
        {rows.map((row) => (
          <div className={styles.popRow} key={row.label}>
            <span className={styles.popLabel}>{row.label}</span>
            <span
              className={`${styles.popValue}${row.muted ? ` ${styles.popMuted}` : ""}`}
            >
              {row.value}
            </span>
          </div>
        ))}
        <div className={styles.popRow}>
          <span className={styles.popLabel}>Visit type</span>
          <span className={styles.popValue}>
            <Tag
              color={
                record?.appointment_for === "consultation" ? "purple" : "blue"
              }
            >
              {record?.appointment_for === "consultation"
                ? "Consultation"
                : "Session"}
            </Tag>
          </span>
        </div>
      </div>

      {!isClosed && (
        <div className={styles.popActions}>
          <Button
            danger
            size="small"
            className={styles.popActionBtn}
            loading={saving === "cancelled"}
            onClick={() => setStatus("cancelled")}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            size="small"
            className={`${styles.popActionBtn} ${styles.popReportedBtn}`}
            loading={saving === "reported"}
            onClick={() => setStatus("reported")}
          >
            Reported
          </Button>
        </div>
      )}
    </div>
  );
};

const OverflowList = ({ items, roleByDoctorId, title, onEdit, onRefresh }) => {
  const groups = useMemo(() => {
    const byDoctor = new Map();
    items.forEach((ev) => {
      const name = ev.record?.doctor_name || "Unassigned";
      if (!byDoctor.has(name)) byDoctor.set(name, []);
      byDoctor.get(name).push(ev);
    });
    return [...byDoctor.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [items]);

  return (
    <div className={styles.overflowCard}>
      <div className={styles.overflowTitle}>
        {title}
        <span className={styles.overflowCount}>
          {items.length} appointment{items.length === 1 ? "" : "s"}
        </span>
      </div>
      <div className={styles.overflowScroll}>
        {groups.map(([doctorName, group]) => (
          <div className={styles.overflowGroup} key={doctorName}>
            <div className={styles.overflowGroupHead}>
              {doctorName}
              {roleByDoctorId[group[0].record?.doctor] && (
                <span className={styles.overflowGroupRole}>
                  {roleByDoctorId[group[0].record.doctor].replace(/_/g, " ")}
                </span>
              )}
            </div>
            {group.map((ev) => (
              <Popover
                key={ev.record.id}
                trigger="click"
                placement="right"
                content={
                  <AppointmentDetails
                    record={ev.record}
                    doctorRole={roleByDoctorId[ev.record?.doctor]}
                    onEdit={onEdit}
                    onRefresh={onRefresh}
                  />
                }
              >
                <button
                  type="button"
                  className={styles.overflowRow}
                  style={eventStyle(ev.record)}
                >
                  <span className={styles.overflowRowTime}>
                    {minutesToLabel(ev.startMin)}
                  </span>
                  <span className={styles.overflowRowName}>
                    {patientName(ev.record)}
                  </span>
                </button>
              </Popover>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

const AppointmentsCalendar = ({
  data,
  loading,
  anchor,
  onAnchorChange,
  mode,
  onModeChange,
  roleByDoctorId = {},
  onRefresh,
}) => {
  const [editRecord, setEditRecord] = useState(null);
  const scrollRef = useRef(null);
  const hasScrolled = useRef(false);
  const monthGridRef = useRef(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [monthRowHeight, setMonthRowHeight] = useState(0);
  const isMobile = useIsMobile();

  const maxLanes = isMobile ? 1 : mode === "day" ? MAX_LANES_DAY : MAX_LANES_WEEK;
  const isMonth = mode === "month";
  const isDay = mode === "day";
  const { start: gridStart, weeks } = useMemo(
    () => calendarRange(anchor, mode),
    [anchor, mode]
  );

  const dayCount = isMonth ? weeks * 7 : isDay ? 1 : 7;
  const days = useMemo(
    () => Array.from({ length: dayCount }, (_, i) => gridStart.add(i, "day")),
    [gridStart, dayCount]
  );

  const eventsByDate = useMemo(() => {
    const buckets = {};
    (data || []).forEach((record) => {
      const startMin = toMinutes(record?.appointment_time);
      if (startMin === null || !record?.appointment_date) return;
      const rawEnd = toMinutes(record?.appointment_end_time);
      const endMin =
        rawEnd !== null && rawEnd > startMin
          ? rawEnd
          : startMin + DEFAULT_DURATION_MIN;
      const key = dayjs(record.appointment_date).format("YYYY-MM-DD");
      (buckets[key] = buckets[key] || []).push({ record, startMin, endMin });
    });
    Object.keys(buckets).forEach((key) => {
      const all = buckets[key];
      buckets[key] = { all, ...layoutOverlaps(all, maxLanes) };
    });
    return buckets;
  }, [data, maxLanes]);

  const spanKey = `${gridStart.format("YYYY-MM-DD")}|${mode}`;
  useEffect(() => {
    hasScrolled.current = false;
  }, [spanKey]);


  useEffect(() => {
    if (!isMonth) return undefined;
    const node = monthGridRef.current;
    if (!node) return undefined;
    const measure = () => {
      const rows = weeks || 1;
      setMonthRowHeight(node.getBoundingClientRect().height / rows);
    };
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [isMonth, weeks]);

  useEffect(() => {
    setPickerOpen(false);
  }, [mode]);

  const monthCapacity = useMemo(() => {
    if (!monthRowHeight) return MONTH_CELL_EVENTS;
    const usable =
      Math.max(monthRowHeight, MONTH_ROW_MIN) -
      MONTH_CELL_PADDING -
      MONTH_DATE_HEIGHT;
    return Math.max(1, Math.floor(usable / MONTH_EVENT_ROW));
  }, [monthRowHeight]);

  useEffect(() => {
    if (isMonth) return undefined;
    const all = Object.values(eventsByDate).flatMap((day) => day.all);
    if (hasScrolled.current || !all.length) return undefined;
    const earliest = Math.min(...all.map((ev) => ev.startMin));
    hasScrolled.current = true;
    const raf = requestAnimationFrame(() => {
      if (!scrollRef.current) return;
      scrollRef.current.scrollTop = Math.max(
        0,
        (earliest / 60) * HOUR_HEIGHT - HOUR_HEIGHT
      );
    });
    return () => cancelAnimationFrame(raf);
  }, [eventsByDate, isMonth]);

  /* Colour legend — only worth showing once more than one doctor is in view. */
  const legend = useMemo(() => {
    const seen = new Map();
    (data || []).forEach((r) => {
      if (r?.doctor && r?.doctor_name && !seen.has(r.doctor)) {
        seen.set(r.doctor, { name: r.doctor_name, color: doctorColor(r) });
      }
    });
    return [...seen.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [data]);

  const today = dayjs();
  const nowMin = today.hour() * 60 + today.minute();

  const rangeLabel = (() => {
    if (isMonth) return anchor.format("MMMM YYYY");
    if (isDay) return gridStart.format("ddd, D MMMM YYYY");
    const last = gridStart.add(6, "day");
    return gridStart.isSame(last, "month")
      ? `${gridStart.format("D")}–${last.format("D MMMM, YYYY")}`
      : `${gridStart.format("D MMM")} – ${last.format("D MMM, YYYY")}`;
  })();

  const stepUnit = isMonth ? "month" : isDay ? "day" : "week";
  const step = (delta) => onAnchorChange(anchor.add(delta, stepUnit));

  const gridVars = {
    "--cal-hour": `${HOUR_HEIGHT}px`,
    "--cal-gutter": `${GUTTER}px`,
    "--cal-days": isDay ? 1 : 7,
  };

  const toolbar = (
    <div className={styles.toolbar}>
      <div className={styles.toolbarNav}>
        <Button
          size="small"
          type="text"
          aria-label={`Previous ${stepUnit}`}
          icon={<LeftOutlined />}
          onClick={() => step(-1)}
        />
        <div className={styles.rangePickerWrap}>
          <Button
            type="text"
            className={styles.rangeLabelBtn}
            onClick={() => setPickerOpen((prev) => !prev)}
          >
            <span className={styles.rangeLabel}>{rangeLabel}</span>
          </Button>
          <DatePicker
            key={mode}
            className={styles.hiddenPicker}
            popupClassName={styles.rangePickerPopup}
            open={pickerOpen}
            onOpenChange={setPickerOpen}
            value={anchor}
            picker={isMonth ? "month" : isDay ? "date" : "week"}
            allowClear={false}
            inputReadOnly
            getPopupContainer={() => document.body}
            onChange={(date) => {
              if (date) onAnchorChange(date);
              setPickerOpen(false);
            }}
          />
        </div>
        <Button
          size="small"
          type="text"
          aria-label={`Next ${stepUnit}`}
          icon={<RightOutlined />}
          onClick={() => step(1)}
        />
      </div>

      {legend.length > 1 && (
        <div className={styles.legend}>
          {legend.map((d) => (
            <span className={styles.legendItem} key={d.name}>
              <span
                className={styles.legendDot}
                style={{ background: d.color.bar }}
              />
              {d.name}
            </span>
          ))}
        </div>
      )}
      <span className={styles.toolbarSpacer} />
      {loading && <Spin size="small" />}
      <Segmented
        size="small"
        value={mode}
        options={[
          { value: "day", label: "Day" },
          { value: "week", label: "Week" },
          { value: "month", label: "Month" },
        ]}
        onChange={onModeChange}
      />
    </div>
  );

  const editDrawer = editRecord ? (
    <EditAppointmentModal
      open={Boolean(editRecord)}
      setOpen={(next) => !next && setEditRecord(null)}
      editData={editRecord}
      refresh={() => {
        setEditRecord(null);
        onRefresh?.();
      }}
    />
  ) : null;

  if (isMonth) {
    return (
      <div className={styles.calendar} style={gridVars}>
        {toolbar}
        <div className={styles.monthWrap}>
          <div className={styles.monthHead}>
            {days.slice(0, 7).map((day) => (
              <div key={day.format("dd")} className={styles.monthHeadCell}>
                {day.format("ddd")}
              </div>
            ))}
          </div>
          <div
            ref={monthGridRef}
            className={styles.monthGrid}
            style={{
              gridTemplateRows: `repeat(${weeks}, minmax(${MONTH_ROW_MIN}px, 1fr))`,
            }}
          >
            {days.map((day) => {
              const key = day.format("YYYY-MM-DD");
              const isToday = day.isSame(today, "day");
              const outside = !day.isSame(anchor, "month");
              const dayEvents = [...(eventsByDate[key]?.all || [])].sort(
                (a, b) => a.startMin - b.startMin
              );
              // Leave a slot for the "+N more" row only when it is needed.
              const visible =
                dayEvents.length > monthCapacity
                  ? Math.max(1, monthCapacity - 1)
                  : monthCapacity;
              const shown = dayEvents.slice(0, visible);
              const hiddenEvents = dayEvents.slice(visible);
              return (
                <div
                  key={key}
                  className={`${styles.monthCell}${
                    outside ? ` ${styles.monthCellOutside}` : ""
                  }`}
                >
                  <div
                    className={`${styles.monthDate}${
                      isToday ? ` ${styles.monthDateToday}` : ""
                    }`}
                  >
                    {day.format("D")}
                  </div>
                  <div className={styles.monthEvents}>
                    {shown.map((ev) => (
                      <Popover
                        key={ev.record.id}
                        trigger="click"
                        placement="right"
                        content={
                          <AppointmentDetails
                            record={ev.record}
                            doctorRole={roleByDoctorId[ev.record?.doctor]}
                            onEdit={setEditRecord}
                            onRefresh={onRefresh}
                          />
                        }
                      >
                        <button
                          type="button"
                          className={styles.monthEvent}
                          style={eventStyle(ev.record)}
                          title={`${patientName(ev.record)} · ${minutesToLabel(
                            ev.startMin
                          )}`}
                        >
                          <span className={styles.monthEventTime}>
                            {minutesToLabel(ev.startMin)}
                          </span>{" "}
                          {patientName(ev.record)}
                        </button>
                      </Popover>
                    ))}
                    {hiddenEvents.length > 0 && (
                      <Popover
                        trigger="click"
                        placement="right"
                        content={
                          <OverflowList
                            items={hiddenEvents}
                            roleByDoctorId={roleByDoctorId}
                            title={day.format("ddd, D MMM YYYY")}
                            onEdit={setEditRecord}
                            onRefresh={onRefresh}
                          />
                        }
                      >
                        <button type="button" className={styles.monthMore}>
                          +{hiddenEvents.length} more
                        </button>
                      </Popover>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        {editDrawer}
      </div>
    );
  }

  return (
    <div className={styles.calendar} style={gridVars}>
      {toolbar}
      <div className={styles.scroll} ref={scrollRef}>
        <div className={styles.head}>
          <div className={styles.headGutter} />
          {days.map((day) => {
            const isToday = day.isSame(today, "day");
            return (
              <div
                key={day.format("YYYY-MM-DD")}
                className={`${styles.dayHead}${
                  isToday ? ` ${styles.dayHeadToday}` : ""
                }`}
              >
                <div className={styles.dayNum}>{day.format("D")}</div>
                <div className={styles.dayName}>{day.format("ddd")}</div>
              </div>
            );
          })}
        </div>

        <div className={styles.body} style={{ height: 24 * HOUR_HEIGHT }}>
          <div className={styles.gutter}>
            {Array.from({ length: 23 }, (_, i) => i + 1).map((hour) => (
              <span
                key={hour}
                className={styles.hourLabel}
                style={{ top: hour * HOUR_HEIGHT }}
              >
                {dayjs().startOf("day").add(hour, "hour").format("h A")}
              </span>
            ))}
          </div>

          {days.map((day) => {
            const key = day.format("YYYY-MM-DD");
            const isToday = day.isSame(today, "day");
            const { placed = [], overflows = [] } = eventsByDate[key] || {};
            return (
              <div
                key={key}
                className={`${styles.dayCol}${
                  isToday ? ` ${styles.dayColToday}` : ""
                }`}
              >
                {isToday && (
                  <div
                    className={styles.nowLine}
                    style={{ top: (nowMin / 60) * HOUR_HEIGHT }}
                  />
                )}
                {overflows.map((group) => {
                  const widthPct = 100 / group.laneCount;
                  return (
                    <Popover
                      key={group.key}
                      trigger="click"
                      placement="right"
                      content={
                        <OverflowList
                          items={group.hidden}
                          roleByDoctorId={roleByDoctorId}
                          title={`${day.format("ddd, D MMM")} · ${minutesToLabel(
                            group.startMin
                          )}`}
                          onEdit={setEditRecord}
                          onRefresh={onRefresh}
                        />
                      }
                    >
                      <button
                        type="button"
                        className={styles.moreChip}
                        title={`${group.hidden.length} more appointments`}
                        style={{
                          top: (group.startMin / 60) * HOUR_HEIGHT,
                          height: Math.max(
                            20,
                            ((group.endMin - group.startMin) / 60) *
                              HOUR_HEIGHT -
                              2
                          ),
                          left: `calc(${group.lane * widthPct}% + 2px)`,
                          width: `calc(${widthPct}% - 4px)`,
                        }}
                      >
                        +{group.hidden.length}
                        <span className={styles.moreChipWord}> more</span>
                      </button>
                    </Popover>
                  );
                })}
                {placed.map((ev) => {
                  const { record } = ev;
                  const widthPct = 100 / ev.laneCount;
                  const height = Math.max(
                    20,
                    ((ev.endMin - ev.startMin) / 60) * HOUR_HEIGHT - 2
                  );
                  const showDoctor = height >= 32 && Boolean(record?.doctor_name);
                  const showTime = height >= 46;
                  return (
                    <Popover
                      key={record.id}
                      trigger="click"
                      placement="right"
                      content={
                        <AppointmentDetails
                          record={record}
                          doctorRole={roleByDoctorId[record?.doctor]}
                          onEdit={setEditRecord}
                          onRefresh={onRefresh}
                        />
                      }
                    >
                      <button
                        type="button"
                        className={`${styles.event}${
                          record?.status === "cancelled"
                            ? ` ${styles.eventCancelled}`
                            : ""
                        }`}
                        title={`${patientName(record)} · ${minutesToLabel(
                          ev.startMin
                        )}`}
                        style={{
                          ...eventStyle(record),
                          top: (ev.startMin / 60) * HOUR_HEIGHT,
                          height,
                          left: `calc(${ev.lane * widthPct}% + 2px)`,
                          width: `calc(${widthPct}% - 4px)`,
                        }}
                      >
                        <div className={styles.eventTitle}>
                          {patientName(record)}
                        </div>
                        {showDoctor && (
                          <div className={styles.eventDoctor}>
                            {record.doctor_name}
                          </div>
                        )}
                        {showTime && (
                          <div className={styles.eventTime}>
                            {minutesToLabel(ev.startMin)}
                          </div>
                        )}
                      </button>
                    </Popover>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
      {editDrawer}
    </div>
  );
};

export default AppointmentsCalendar;
